import React from "react";
import {
    Button,
    checkAndSetError,
    clearInputFormErrorFields,
    convertInputFormToObject,
    InputForm,
    inputFormIsValid,
    NumberField,
    SwitchField,
    TextArea,
    TextField,
    useInputForm,
} from "../../../../Components/Inputs";
import {GridCell, gridConstants} from "../../../../Components/Grid";
import {braillerCatalogItemModel} from "./braillerCatalogItemFactory";
import {BraillerInventoryView} from "./BraillerInventoryItem/BraillerInventoryView";
import type {BraillerInventoryItemDto} from "../brailler.types";

interface BraillerCatalogItemViewProps {
    canManageCatalog: boolean;
    handleSaveBraillerCatalogItem: (item: Record<string, unknown>) => void;
    handleReturn: () => void;
    braillerCatalogItem: Record<string, unknown>;
    inventoryItems: BraillerInventoryItemDto[];
    onInventoryChange: (items: BraillerInventoryItemDto[]) => void;
}

export const BraillerCatalogItemView = ({
                                            canManageCatalog,
                                            handleSaveBraillerCatalogItem,
                                            handleReturn,
                                            braillerCatalogItem,
                                            inventoryItems,
                                            onInventoryChange,
                                        }: BraillerCatalogItemViewProps) => {

    const braillerCatalogItemForm = useInputForm(braillerCatalogItem);

    const handleSubmitClick = () => {
        clearInputFormErrorFields(braillerCatalogItemForm);

        checkAndSetError([
            braillerCatalogItemForm.title,
            braillerCatalogItemForm.model,
        ]);

        if (inputFormIsValid(braillerCatalogItemForm)) {
            const convertedFormData = convertInputFormToObject(braillerCatalogItemForm);
            const braillerCatalogItem = braillerCatalogItemModel(convertedFormData);
            braillerCatalogItem.inventoryItems = inventoryItems;

            handleSaveBraillerCatalogItem(braillerCatalogItem);
        }
    };

    const handleInventoryChange = (newInventoryItems: BraillerInventoryItemDto[]) => {
        onInventoryChange(newInventoryItems);
    };

    const handleSwitchChange = (fieldName: string) => (e: unknown) => {
        const isChecked = (e as React.ChangeEvent<HTMLInputElement>).target.checked;
        braillerCatalogItemForm[fieldName].setValue(String(isChecked));
    };

    const getBooleanValue = (fieldName: string) => {
        const value = braillerCatalogItemForm[fieldName].value;
        return value === 'true' || value === '1';
    };

    return (
        <>
            <InputForm name="braillerCatalogItemForm" key={String(braillerCatalogItem.id || 'new')}>
                <TextField {...braillerCatalogItemForm.title} medium_columns={gridConstants.column.SIX}
                           disabled={!canManageCatalog}/>
                <TextField {...braillerCatalogItemForm.model} medium_columns={gridConstants.column.SIX}
                           disabled={!canManageCatalog}/>
                <NumberField {...braillerCatalogItemForm.cost} medium_columns={gridConstants.column.SIX}
                           disabled={!canManageCatalog}/>
                <NumberField {...braillerCatalogItemForm.repairCost} medium_columns={gridConstants.column.SIX}
                           disabled={!canManageCatalog}/>
                <SwitchField
                    {...braillerCatalogItemForm.isActive}
                    value={getBooleanValue('isActive')}
                    onChange={handleSwitchChange('isActive')}
                    medium_columns={gridConstants.column.THREE}
                    disabled={!canManageCatalog}
                />
                <SwitchField
                    {...braillerCatalogItemForm.allowRepairs}
                    value={getBooleanValue('allowRepairs')}
                    onChange={handleSwitchChange('allowRepairs')}
                    medium_columns={gridConstants.column.THREE}
                    disabled={!canManageCatalog}
                />
                <TextArea {...braillerCatalogItemForm.description} medium_columns={gridConstants.column.TWELVE}
                          disabled={!canManageCatalog}/>
            </InputForm>

            <hr/>

            <BraillerInventoryView
                inventoryItems={inventoryItems}
                onInventoryChange={handleInventoryChange}
                catalogItemId={String(braillerCatalogItem.id || '')}
                title={String(braillerCatalogItem.title || '')}
            />

            <GridCell className="text-center">
                {canManageCatalog && (
                    <Button isPrimary isCollapsed label={'Save Changes'}
                            name="btnSubmit" onClick={handleSubmitClick}/>
                )}
                <Button isCollapsed label="Cancel" name="btnCancel" onClick={handleReturn}/>
            </GridCell>
        </>
    );
};

